import {
	deleteGoalAndInterventionsOptions,
	GoalDefinitionTypeEnum,
	type DeleteGoalAndInterventionsCommand,
	type GoalViewModel,
	type InterventionViewModel
} from '@generated';
import { SectionListRow } from '~/shared/section/section-list/SectionListRow';
import { useQuery } from '@tanstack/react-query';
import { useMutation } from '~/util/http/useMutation';
import { Prompt } from '~/shared/components/Prompt';
import { EditPredefinedGoalFormModal } from './EditPredefinedGoalFormModal';
import { Button } from '~/shared/components/buttons/Button';
import { Calendar, Paperclip } from 'lucide-react';
import { AttachInterventionFormModal } from '../interventions/AttachInterventionFormModal';
import { useGoalsAndnterventionsForOrder } from '../queries';
import { useFullOrder } from '~/features/orders/orders/FullOrderContext';
import { InterventionOrderRow } from '../interventions/InterventionOrderRow';
import { GroupByInterventionTypes } from '../interventions/GroupByInterventionTypes';
import { EditCustomGoalFormModal } from './EditCustomGoalFormModal';

export function GoalOrderRow({ goal }: { goal: GoalViewModel }) {
	const { fullOrder } = useFullOrder();
	const { refetch } = useQuery(useGoalsAndnterventionsForOrder(fullOrder.id));
	const deleteGoal = useMutation<void, DeleteGoalAndInterventionsCommand>(deleteGoalAndInterventionsOptions);
	const onDeleteGoal = () => {
		deleteGoal.mutate(
			{ orderId: fullOrder.id, goalId: goal.id },
			{
				onSuccess: () => {
					refetch();
				}
			}
		);
	};

	const isOrderGoal = goal.orderId === fullOrder.id;
	const interventionsForGoal = fullOrder.interventions.filter(x => goal.interventionIds.includes(x.id));
	const expectedEndDate = interventionsForGoal.reduce((acc, x) => {
		if (x.projectedEndDate && (!acc || x.projectedEndDate > acc)) {
			return x.projectedEndDate;
		}
		return acc;
	}, null as Date | null);

	return (
		<SectionListRow
			label={
				<div className="flex items-center justify-between gap-2">
					{goal.definitionText}
					{expectedEndDate && (
						<>
							<Calendar className="size-4 text-gray-400" />
							<span className="text-sm text-gray-500"> {expectedEndDate.toLocaleDateString()}</span>
						</>
					)}
				</div>
			}
			buttonSlot={
				goal.invariants.canBeAttachedTo && (
					<Button
						aria-label="Attach Intervention"
						size="xs"
						appearance="ghost"
						intent="neutralLight"
						useSuspense
						Icon={Paperclip}
						useTooltip
						renderModal={closeModal => <AttachInterventionFormModal goalId={goal.id} fullOrder={fullOrder} closeModal={closeModal} />}
					/>
				)
			}
			removeButtonProps={
				isOrderGoal
					? {
							label: 'Remove Goal',
							isPending: deleteGoal.isPending,
							renderModal: closeModal => (
								<Prompt
									title="Remove Goal"
									text="Are you sure you want to remove this goal & interventions?"
									onAction={() => {
										onDeleteGoal();
										closeModal();
									}}
									onCancel={closeModal}
								/>
							)
					  }
					: undefined
			}
			editButtonProps={
				isOrderGoal &&
				((goal.goalDefinition && goal.goalDefinition.hasDefinitionFields) || goal.goalDefinitionTypeId === GoalDefinitionTypeEnum.Custom)
					? {
							label: 'Edit Goal',
							renderModal: closeModal =>
								goal.goalDefinitionTypeId === GoalDefinitionTypeEnum.Custom ? (
									<EditCustomGoalFormModal goalId={goal.id} fullOrder={fullOrder} closeModal={closeModal} />
								) : (
									<EditPredefinedGoalFormModal goalId={goal.id} fullOrder={fullOrder} closeModal={closeModal} />
								)
					  }
					: undefined
			}
		>
			<ul className="space-y-3">
				<GroupByInterventionTypes<InterventionViewModel>
					displayIntervention={x => <InterventionOrderRow key={x.id} intervention={x} goal={goal} />}
					interventions={interventionsForGoal}
					expandOnLoad
				/>
			</ul>
		</SectionListRow>
	);
}
